import React from "react";
import { Link } from "react-router-dom";
import { HeartOff } from "lucide-react";
import { useSelector } from "react-redux";

import styles from "./FavoriteItem.module.scss";
import { SaleItemInterface } from "../../../common/types/interfaces";
import { itemRoute } from "../../../common/utils/constants";
import { selectClusterList } from "../../../store/slices/clusterListSlice";
import {
  getClusterImageUrl,
  getFirstImage,
} from "../../../common/utils/functions/getImageUri";

interface Props {
  item: SaleItemInterface;
  removing?: boolean;
  unavailable?: boolean;
  onRemove: (id: number) => void;
  lastItemRef?: React.RefCallback<HTMLElement>;
}

const minPrice = (item: SaleItemInterface) => {
  const prices = (item.prices || []).map((p) => Number(p.price));
  if (prices.length === 0) return null;
  return Math.min(...prices);
};

export const FavoriteItem: React.FC<Props> = ({
  item,
  removing,
  unavailable,
  onRemove,
  lastItemRef,
}) => {
  const clusters = useSelector(selectClusterList);
  const listCluster = clusters.find((c) => c.id === item.clusterId);
  const cluster = listCluster || item.cluster;

  const image = getFirstImage(item.files);
  const price = minPrice(item);
  const isOutOfStock =
    !unavailable && item.isInStockInSelectedCity === false;

  return (
    <div
      ref={lastItemRef as React.Ref<HTMLDivElement>}
      className={`${styles.Row} ${unavailable ? styles.Unavailable : ""}`}
    >
      <span className={styles.BgText} aria-hidden="true">
        {item.name}
      </span>

      <Link to={itemRoute(item.id)} className={styles.ImageLink}>
        <div className={styles.ImageWrap}>
          {image && cluster ? (
            <img
              src={getClusterImageUrl(cluster as any, image)}
              alt={item.name}
              className={`${styles.Image} ${
                unavailable ? styles.ImageDim : ""
              }`}
              loading="lazy"
            />
          ) : (
            <div className={styles.ImagePlaceholder} />
          )}
          {/* {unavailable && (
            <span className={styles.CityBadge}>Другой город</span>
          )} */}
        </div>
      </Link>

      <div className={styles.RowInfo}>
        <div className={styles.TopLine}>
          <Link to={itemRoute(item.id)} className={styles.Name}>
            {item.name}
          </Link>
          {item.category?.name && (
            <span className={styles.Category}>{item.category.name}</span>
          )}
          {isOutOfStock && (
            <span className={styles.OutOfStockBadge}>Нет в наличии</span>
          )}
        </div>

        <div className={styles.ClusterLine}>
          {cluster && (
            <>
              {cluster.domain && (
                <img
                  src={`https://${cluster.domain}/icons/logo512.png`}
                  alt={cluster.name}
                  className={styles.ClusterLogo}
                />
              )}
              <span className={styles.ClusterName}>{cluster.name}</span>
            </>
          )}
          {unavailable && (
            <span className={styles.UnavailableBadge}>В другом городе</span>
          )}
        </div>

        <div className={styles.BottomLine}>
          {price !== null && <span className={styles.Price}>от {price} ₽</span>}
          <button
            type="button"
            className={styles.Remove}
            onClick={() => onRemove(item.id)}
            disabled={removing}
            aria-label="Убрать из избранного"
            title="Убрать из избранного"
          >
            <HeartOff size={24} strokeWidth={1.75} />
          </button>
        </div>
      </div>
    </div>
  );
};